import { useResolve } from '@/hooks/useResolve'
import { pad } from './primitives'

/**
 * FLUX.1 dev, one 1024×1024 image, as Magic Lamp's research priced it on
 * 2026-09-23 (magiclamp docs/research/magiclamp-provider-universe.md; design/web
 * mocks/catalog.ts). Per-megapixel prices are shown at 1 MP. Magic Lamp's first
 * version routes between Runware and fal (docs/plan build order); the other hosts
 * are shown for reference only, so they carry no route mark.
 */
const OFFERS = [
  { provider: 'Runware', usd: '0.0038', routed: true },
  { provider: 'SiliconFlow', usd: '0.014', routed: false },
  { provider: 'RunPod', usd: '0.020', routed: false },
  { provider: 'fal', usd: '0.025', routed: true },
  { provider: 'AIML API', usd: '0.0325', routed: false },
] as const

const PROMISES = [
  { title: 'Every offer, tracked', body: 'Prices, promos and provider health, tracked for every offer.' },
  { title: 'Cheapest healthy route', body: 'Each job goes to the cheapest equivalent offer that is up right now.' },
  { title: 'Fallback on failure', body: 'A failed attempt moves to the next offer and is not charged.' },
  { title: 'Settled on real cost', body: 'Every job settles on what the provider actually charged, not an estimate.' },
] as const

/** The Magic Lamp mark: a lamp's spout with a flame drawn as a single drop of light. */
function LampMark({ className = 'size-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path d="M12.4 2.6c1.9 1.8 2.1 3.8.8 5.4-.5.6-1.3.8-1.9.4-1.1-.7-1.3-2.4 1.1-5.8z" fill="var(--lamp)" />
      <path
        d="M2 12.4h13.6c1 0 1.8-.3 2.5-.9l3.1-2.6c.5-.4 1.2.1.9.7l-1.7 3.7c-1 2-2.5 3.6-4.5 4.5-1.4.6-3 .9-4.6.9H9.6C5.4 18.7 2 16.1 2 12.4z"
        fill="currentColor"
      />
      <path d="M7.6 20.6h7.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  )
}

export function MagicLamp() {
  const title = useResolve<HTMLHeadingElement>()
  const fal = Number(OFFERS[3].usd)
  const best = Number(OFFERS[0].usd)
  // 0.025 / 0.0038 = 6.58: rounded down to the half, never up.
  const cheaper = Math.floor((fal / best) * 2) / 2

  return (
    <section id="magic-lamp" className="mt-28 scroll-mt-20" aria-labelledby="magic-lamp-heading">
      {/* Full-bleed band: no box or corners, the panel tone edge to edge with one amber glow in it. */}
      <div className="relative overflow-hidden bg-panel">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_75%_at_72%_30%,rgba(242,165,22,0.2),rgba(242,165,22,0.07)_45%,transparent_75%)]"
        />
        <div className="landing-shell relative py-16 md:py-24">
          <div className="relative flex flex-wrap items-center gap-3">
            <LampMark className="size-7 text-ink" />
            <span className="text-[15px] font-medium uppercase tracking-[0.14em]">Magic Lamp</span>
            <span className="mono-label ml-auto rounded-[3px] border border-rule-strong px-2 py-1.5 text-ink-3">
              Coming to Genie cloud
            </span>
          </div>

          <h2 ref={title.ref} data-grade="100" onMouseEnter={title.replay} id="magic-lamp-heading" className="display relative mt-10 max-w-[14ch] text-[clamp(34px,6vw,88px)]">
            The cheapest route, every time
          </h2>

          <div className="relative mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:items-start">
            <div>
              <p className="max-w-md text-[16px] leading-relaxed text-ink-2">
                Magic Lamp is our media model exchange, and it is coming to Genie’s cloud image models first. It
                compares equivalent offers across providers, runs each job on the cheapest healthy one, and falls back if
                it fails.
              </p>
              <p className="mt-8 flex items-center gap-4 border-t border-dashed border-rule pt-8">
                <span className="display text-[clamp(44px,5vw,64px)]">{cheaper}×</span>
                <span className="max-w-[30ch] text-[14px] leading-snug text-ink-2">
                  cheaper for the same FLUX.1 dev image than fal’s list price
                </span>
              </p>
            </div>

            <div className="rounded-[3px] border border-rule bg-card">
              <div className="flex items-center justify-between border-b border-dashed border-rule px-5 py-3">
                <span className="mono-label text-ink">FLUX.1 dev · compared offers</span>
                <span className="mono-label text-ink-3">Prices seen 23 Sep 2026</span>
              </div>
              <ol>
                {OFFERS.map((o, i) => {
                  const route = i === 0
                  return (
                    <li
                      key={o.provider}
                      className={`grid grid-cols-[28px_1fr_auto_auto] items-center gap-4 border-t border-dashed border-rule px-5 py-3 first:border-t-0 ${
                        route ? 'bg-[var(--lamp-glow)]' : ''
                      }`}
                    >
                      <span className="num text-[12px] text-ink-3">{pad(i + 1)}</span>
                      <span className="text-[15px]">{o.provider}</span>
                      <span className={`mono-label ${route ? 'text-lamp-ink' : 'text-ink-3'}`}>
                        {route ? 'Route' : o.routed ? '' : 'Reference'}
                      </span>
                      <span className="num text-right text-[15px]">${o.usd}</span>
                    </li>
                  )
                })}
              </ol>
              <p className="mono-label border-t border-dashed border-rule px-5 py-3 text-ink-3">
                1024×1024 image · per-megapixel prices at 1 MP · each host’s default settings
              </p>
            </div>
          </div>

          <ul className="relative mt-14 grid border-t border-rule sm:grid-cols-2 lg:grid-cols-4">
            {PROMISES.map((p, i) => (
              <li key={p.title} className="flex flex-col gap-2 py-6 pr-6 lg:border-l lg:border-rule lg:pl-6 lg:first:border-l-0 lg:first:pl-0">
                <span className="num text-[12px] text-ink-3">{pad(i + 1)}</span>
                <span className="text-[16px] tracking-[-0.01em]">{p.title}</span>
                <span className="text-[13px] leading-relaxed text-ink-2">{p.body}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
